import {Cache} from 'aws-amplify/utils';
import {useSearchParams} from "react-router-dom";
import {useAuth} from "./useAuth.ts";
import {isAllowedRedirectUrl} from "../utils/isAllowedRedirectUrl.ts";

export const useReturnTo = () => {

    const [params] = useSearchParams();

    const {
        user,
        idToken,
        accessToken
    } = useAuth();


    // FNP-864: validate BEFORE caching. `returnTo` is attacker-controllable and
    // is read back later to build a URL carrying both raw tokens.
    const saveReturnTo = () => {
        const returnTo = params.get('returnTo')
        if (isAllowedRedirectUrl(returnTo)) {
            Cache.setItem('returnTo', returnTo)
        }
    };

    const getReturnTo = async () => {
        return await Cache.getItem('returnTo')
    };

    // FNP-864: both generators re-validate on READ. The cache is untrusted
    // input — it is written before the user authenticates, and an entry may
    // predate this guard. Fail closed by returning '' (no redirect), and never
    // log the resulting URL: it carries both raw tokens.
    const buildRedirectUrl = (returnTo: string) => {
        if (typeof accessToken === 'undefined') {
            throw new Error('accessToken is undefined')
        }

        if (typeof idToken === 'undefined') {
            throw new Error('idToken is undefined')
        }

        Cache.removeItem('returnTo');

        const fragment = new URLSearchParams();
        fragment.append('expires_in', '3600');
        fragment.append('token_type', 'Bearer');
        fragment.append('id_token', idToken.toString());
        fragment.append('access_token', accessToken.toString());

        return returnTo + '#' + fragment.toString();
    };

    const generateRedirectUrl = async () => {

        const returnTo = await getReturnTo()

        if (!isAllowedRedirectUrl(returnTo)) {
            await Cache.removeItem('returnTo');
            return ''
        }

        if (typeof user === 'undefined') {
            setTimeout(() => {
                return;
            }, 1000)
            //throw new Error('user is undefined')
        }

        return buildRedirectUrl(returnTo);
    };

    const generateRedirectUrlWithUser = async () => {

        // Was missing its `await` before FNP-864, so this resolved a Promise
        // object rather than the cached value.
        const returnTo = await Cache.getItem('returnTo');

        if (!isAllowedRedirectUrl(returnTo)) {
            await Cache.removeItem('returnTo');
            return ''
        }

        return buildRedirectUrl(returnTo);
    };

    return {
        saveReturnTo,
        getReturnTo,
        generateRedirectUrl,
        generateRedirectUrlWithUser
    };
};
